package com.schoolconnect.app.feature.schoolconfig

import com.schoolconnect.app.datasource.network.response.SchoolConfigListItemResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.config.ConfigRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SchoolConfigViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var editJob: Job? = null
    private val _uiState = MutableStateFlow(SchoolConfigUiState(isLoading = true))
    val uiState: StateFlow<SchoolConfigUiState> = _uiState.asStateFlow()

    init { loadPage() }

    fun onEvent(event: SchoolConfigUiEvent) {
        when (event) {
            is SchoolConfigUiEvent.SearchChanged -> _uiState.update {
                it.copy(searchQuery = event.query.trimStart(), pageIndex = 0, feedbackMessage = "")
            }.also { loadPage(true) }
            is SchoolConfigUiEvent.StatusFilterChanged -> _uiState.update {
                it.copy(statusFilter = event.status, pageIndex = 0, feedbackMessage = "")
            }.also { loadPage(true) }
            SchoolConfigUiEvent.FiltersReset -> _uiState.update {
                it.copy(
                    searchQuery = "",
                    statusFilter = SchoolConfigStatusFilter.ALL,
                    pageIndex = 0,
                    feedbackMessage = ""
                )
            }.also { loadPage(true) }
            is SchoolConfigUiEvent.PageChanged -> _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }.also { loadPage() }
            is SchoolConfigUiEvent.PageSizeChanged -> _uiState.update {
                val size = event.pageSize.takeIf { it in PaginationPolicy.PAGE_SIZE_OPTIONS } ?: PaginationPolicy.DEFAULT_PAGE_SIZE
                it.copy(pageSize = size, pageIndex = 0)
            }.also { loadPage() }
            is SchoolConfigUiEvent.SortChanged -> _uiState.update {
                val ascending = if (it.sortField == event.field) !it.sortAscending else true
                it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
            }.also { loadPage(true) }
            is SchoolConfigUiEvent.ViewClicked -> loadViewConfig(event.row)
            SchoolConfigUiEvent.ViewDismissed -> {
                editJob?.cancel()
                _uiState.update { it.copy(viewingRow = null, viewingConfig = null) }
            }
            is SchoolConfigUiEvent.EditClicked -> loadEditConfig(event.row)
            is SchoolConfigUiEvent.AcademicYearSelected -> reloadAcademicYear(event.id, event.label)
            SchoolConfigUiEvent.EditDismissed -> {
                editJob?.cancel()
                _uiState.update { it.copy(editingRow = null, editingConfig = null, isSaving = false) }
            }
            is SchoolConfigUiEvent.EditSaved -> saveEditConfig(event.draft)
            SchoolConfigUiEvent.HolidayTemplateRequested -> downloadHolidayTemplate()
            is SchoolConfigUiEvent.HolidayImportRequested -> importHolidays(event.fileName, event.bytes)
            is SchoolConfigUiEvent.OperationFailed -> _uiState.update { it.copy(isSaving = false, errorMessage = event.message) }
            SchoolConfigUiEvent.FeedbackDismissed -> _uiState.update { it.copy(feedbackMessage = "", errorMessage = "") }
            SchoolConfigUiEvent.RetryRequested -> loadPage(true)
        }
    }

    private fun loadPage(forceRemote: Boolean = false) {
        loadJob?.cancel()
        val requested = _uiState.value
        // Search is still client-filtered (API has no q); status + sort are server-side.
        val clientFiltering = requested.searchQuery.isNotBlank()
        val requestPageSize = if (clientFiltering) 100 else requested.pageSize
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        loadJob = scope.launch {
            ConfigRepository.loadSchoolConfigPage(
                pageIndex = if (clientFiltering) 0 else requested.pageIndex,
                pageSize = requestPageSize,
                sortBy = requested.sortField.apiField,
                order = if (requested.sortAscending) "asc" else "desc",
                searchQuery = requested.searchQuery,
                isActive = requested.statusFilter.toIsActiveParam(),
                forceRemote = forceRemote
            ).onSuccess { items ->
                val query = requested.searchQuery.trim()
                val mapped = items.map { it.toUiRow() }.filter { row ->
                    query.isBlank() || listOf(row.schoolName, row.code, row.city).any { value ->
                        value.contains(query, ignoreCase = true)
                    }
                }
                val ordered = if (!clientFiltering) {
                    mapped
                } else {
                    when (requested.sortField) {
                        SchoolConfigSortField.SCHOOL_NAME -> {
                            val sorted = mapped.sortedBy { it.schoolName.lowercase() }
                            if (requested.sortAscending) sorted else sorted.asReversed()
                        }
                        SchoolConfigSortField.CODE -> {
                            val sorted = mapped.sortedBy { it.code.lowercase() }
                            if (requested.sortAscending) sorted else sorted.asReversed()
                        }
                        SchoolConfigSortField.CITY -> {
                            val sorted = mapped.sortedBy { it.city.lowercase() }
                            if (requested.sortAscending) sorted else sorted.asReversed()
                        }
                        SchoolConfigSortField.STATUS -> {
                            val sorted = mapped.sortedBy { it.isActive }
                            if (requested.sortAscending) sorted else sorted.asReversed()
                        }
                        SchoolConfigSortField.UPDATED_DATE -> mapped
                    }
                }
                val total = if (clientFiltering) ordered.size else items.serverTotalCountOrNull() ?: ordered.size
                val pageRows = if (clientFiltering) {
                    ordered.drop(requested.pageIndex * requested.pageSize).take(requested.pageSize)
                } else {
                    ordered
                }
                _uiState.update { it.copy(rows = pageRows, totalCount = total, isLoading = false, errorMessage = "") }
            }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load school configuration." }) }
                }
        }
    }

    private fun loadViewConfig(row: SchoolConfigRow) {
        editJob?.cancel()
        _uiState.update {
            it.copy(
                viewingRow = row,
                viewingConfig = null,
                editingRow = null,
                editingConfig = null,
                isSaving = false,
                feedbackMessage = "",
                errorMessage = ""
            )
        }
        editJob = scope.launch {
            ConfigRepository.loadSchoolConfig(
                schoolId = row.id,
                academicYear = null,
                academicYearId = null,
                includeAcademicYearOptions = false
            )
                .onSuccess { config -> _uiState.update { it.copy(viewingConfig = config) } }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update {
                        it.copy(
                            viewingRow = null,
                            viewingConfig = null,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load configuration." }
                        )
                    }
                }
        }
    }

    private fun loadEditConfig(row: SchoolConfigRow, academicYear: String? = null, academicYearId: String? = null) {
        editJob?.cancel()
        _uiState.update {
            it.copy(
                editingRow = row,
                editingConfig = null,
                viewingRow = null,
                viewingConfig = null,
                feedbackMessage = "",
                errorMessage = ""
            )
        }
        editJob = scope.launch {
            ConfigRepository.loadSchoolConfig(row.id, academicYear, academicYearId)
                .onSuccess { config -> _uiState.update { it.copy(editingConfig = config) } }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(editingRow = null, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load configuration." }) }
                }
        }
    }

    private fun reloadAcademicYear(id: String, label: String) {
        val row = _uiState.value.editingRow ?: return
        editJob?.cancel()
        editJob = scope.launch {
            ConfigRepository.loadSchoolConfig(row.id, label, id)
                .onSuccess { config -> _uiState.update { it.copy(editingConfig = config, errorMessage = "") } }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load the selected academic year." }) }
                }
        }
    }

    private fun saveEditConfig(draft: SchoolConfigEditData) {
        val row = _uiState.value.editingRow ?: return
        editJob?.cancel()
        _uiState.update { it.copy(isSaving = true, feedbackMessage = "", errorMessage = "") }
        editJob = scope.launch {
            ConfigRepository.saveSchoolConfig(row.id, draft)
                .onSuccess {
                    _uiState.update { it.copy(editingRow = null, editingConfig = null, isSaving = false, feedbackMessage = "Configuration saved successfully.") }
                    loadPage(true)
                }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(isSaving = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to save configuration." }) }
                }
        }
    }

    private fun downloadHolidayTemplate() {
        editJob?.cancel()
        _uiState.update { it.copy(isSaving = true, feedbackMessage = "", errorMessage = "") }
        editJob = scope.launch {
            ConfigRepository.downloadHolidayTemplate()
                .onSuccess { _uiState.update { it.copy(isSaving = false, feedbackMessage = "Holiday template downloaded.") } }
                .onFailure { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(isSaving = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to download holiday template." }) }
                }
        }
    }

    private fun importHolidays(fileName: String, bytes: ByteArray) {
        val row = _uiState.value.editingRow ?: return
        editJob?.cancel()
        _uiState.update { it.copy(isSaving = true, feedbackMessage = "", errorMessage = "") }
        editJob = scope.launch {
            ConfigRepository.importHolidays(row.id, fileName, bytes, _uiState.value.editingConfig?.academicYearId).fold(
                onSuccess = { message ->
                    val editing = _uiState.value.editingConfig
                    ConfigRepository.loadSchoolConfig(row.id, editing?.academicYear, editing?.academicYearId).fold(
                        onSuccess = { config -> _uiState.update { it.copy(editingConfig = config, isSaving = false, feedbackMessage = message) } },
                        onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, errorMessage = throwable.message.orEmpty().ifBlank { "Holidays imported, but the configuration could not be refreshed." }) } }
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) throw throwable
                    _uiState.update { it.copy(isSaving = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to import holidays." }) }
                }
            )
        }
    }

    fun clear() { loadJob?.cancel(); editJob?.cancel(); scope.cancel() }

    private fun SchoolConfigListItemResponse.toUiRow() = SchoolConfigRow(
        id = (schoolId ?: id).orEmpty(),
        schoolName = name.orEmpty().ifBlank { "Unnamed school" },
        code = this.code.orEmpty().ifBlank { "-" },
        city = city.orEmpty().ifBlank { "-" },
        isActive = this.isActive != false,
        updatedDate = formatApiDate(updatedAt.orEmpty()).ifBlank { "-" }
    )

    private fun formatApiDate(value: String): String {
        val parts = value.trim().substringBefore("T").split("-")
        if (parts.size != 3 || parts.any { it.isBlank() }) return value.trim()
        val month = parts[1].toIntOrNull()?.takeIf { it in 1..12 } ?: return value.trim()
        val monthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        return "${parts[2].take(2).padStart(2, '0')} ${monthNames[month - 1]} ${parts[0]}"
    }
}
